import React from 'react';
import { HoverExplanation, PillTone, StatusPill } from './BatiwebForwardCell';

// Colonne « Waterfall » de l'onglet No match : où en est le lead (Bourse du Lead -> partenaire ->
// offre locked) en une frise, une pastille et au plus une ligne ; le reste est dans la bulle au survol.
// Calculé côté back (services/integration/leadJourney.ts).

export type JourneyStep = 'bourse' | 'partner' | 'waterfall' | 'closed';
export type JourneyStatus =
    | 'on_bourse'
    | 'bourse_after_partner'
    | 'partner_sending'
    | 'partner_pending'
    | 'partner_sold'
    | 'waterfall_queued'
    | 'waterfall_assigned'
    | 'bought_bourse'
    | 'forwarded'
    | 'off_market';

export interface LeadJourney {
    step: JourneyStep;
    status: JourneyStatus;
    partner?: string;
    at?: string | null;
    offerName?: string | null;
    buyerEmail?: string | null;
    // not_sent : jamais proposé au partenaire, pris directement par le waterfall (pas un refus).
    refusal?: 'unsold' | 'timeout' | 'not_sellable' | 'blocked' | 'not_sent' | null;
    overdue?: boolean;
    offMarketReason?: string | null;
}

const REFUSALS: Record<NonNullable<LeadJourney['refusal']>, string> = {
    unsold: 'annulé',
    timeout: 'sans réponse (5 j ouvrés)',
    not_sellable: 'non vendable',
    blocked: 'bloqué, hors partenaire',
    not_sent: 'jamais proposé',
};

const OFF_MARKET: Record<string, string> = {
    expired: 'plus de 14 jours',
    no_seller: 'sans vendeur',
    no_price: 'sans prix vendeur',
};

// `label` peut citer le partenaire ({p}).
const STATUS: Record<JourneyStatus, { label: string; tone: PillTone; live?: boolean; meaning: string }> = {
    on_bourse: { label: 'Bourse', tone: 'blue', meaning: "En vente sur la Bourse du Lead. Pas encore proposé à un partenaire." },
    bourse_after_partner: { label: 'Bourse · refusé {p}', tone: 'amber', meaning: "Refusé par le partenaire, aucune offre du waterfall ne correspond : de nouveau en vente sur la Bourse du Lead." },
    partner_sending: { label: '{p} · envoi', tone: 'blue', live: true, meaning: "En cours d'envoi au partenaire, retiré de la Bourse." },
    partner_pending: { label: '{p} · en attente', tone: 'blue', live: true, meaning: "Chez le partenaire, sans réponse pour l'instant. Après 5 jours ouvrés sans réponse, il passe au waterfall." },
    partner_sold: { label: '{p} · vendu', tone: 'green', meaning: 'Acheté par le partenaire. Le vendeur est payé à son prix minimum.' },
    waterfall_queued: { label: 'Waterfall · en file', tone: 'amber', live: true, meaning: "Refusé par le partenaire, ou jamais proposé. Sera attribué à l'offre locked indiquée au prochain passage (chaque jour après 9h, ou depuis la page Waterfall)." },
    waterfall_assigned: { label: 'Waterfall · attribué', tone: 'green', meaning: "Refusé par le partenaire (ou jamais proposé) puis attribué à une offre locked. Le vendeur touche son prix minimum après 48 h de validation." },
    bought_bourse: { label: 'Bourse · vendu', tone: 'green', meaning: 'Acheté par un acheteur sur la Bourse du Lead.' },
    forwarded: { label: 'Transféré', tone: 'gray', meaning: "Transféré à une offre depuis le back-office : c'est sa copie qui est vendue." },
    off_market: { label: 'Invendu', tone: 'gray', meaning: "Plus visible sur la Bourse du Lead et vendu à personne." },
};

// Frise : Bourse -> Partenaire -> Waterfall -> Vendu. Seules les étapes réellement traversées
// sont colorées : un lead vendu à Batiweb ne passe pas par le waterfall, un lead acheté sur la
// Bourse sans refus préalable ne passe ni par le partenaire ni par le waterfall, un lead jamais
// proposé au partenaire va directement de la Bourse au waterfall.
const FRISE = ['Bourse', 'Partenaire', 'Waterfall', 'Vendu'];

function frisePath(journey: LeadJourney): number[] {
    const refusedByPartner = !!journey.refusal && journey.refusal !== 'not_sent';
    switch (journey.status) {
        case 'partner_sending':
        case 'partner_pending':
            return [0, 1];
        case 'partner_sold':
            return [0, 1, 3];
        case 'waterfall_queued':
            return refusedByPartner ? [0, 1, 2] : [0, 2];
        case 'waterfall_assigned':
            return refusedByPartner ? [0, 1, 2, 3] : [0, 2, 3];
        case 'bought_bourse':
            return refusedByPartner ? [0, 1, 3] : [0, 3];
        case 'forwarded':
            return [0, 3];
        case 'bourse_after_partner':
            // Refusé par le partenaire puis revenu sur la Bourse : étape en cours = Bourse.
            return [0, 1, 0];
        default:
            return [0];
    }
}

const Frise = ({ journey }: { journey: LeadJourney }) => {
    const path = frisePath(journey);
    const current = path[path.length - 1];
    const visited = new Set(path);
    const sold = current === FRISE.length - 1;
    return (
        <div className="flex items-center" aria-label={`Étape : ${FRISE[current]}`}>
            {FRISE.map((step, i) => (
                <React.Fragment key={step}>
                    {i > 0 && <span className={`h-px w-3 ${visited.has(i) ? 'bg-[#6170F5]' : 'bg-[#E3E6EC] dark:bg-white/10'}`} />}
                    <span
                        title={visited.has(i) ? step : `${step} (non traversé)`}
                        className={`rounded-full ${
                            i === current
                                ? `h-2 w-2 ring-2 ${sold ? 'bg-[#12B886] ring-[#12B886]/25' : 'bg-[#6170F5] ring-[#6170F5]/25'}`
                                : visited.has(i)
                                  ? 'h-1.5 w-1.5 bg-[#6170F5]'
                                  : 'h-1.5 w-1.5 bg-[#E3E6EC] dark:bg-white/15'
                        }`}
                    />
                </React.Fragment>
            ))}
        </div>
    );
};

const shortDate = (value?: string | null) =>
    value ? new Date(value).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : null;

// La seule info utile sous la pastille : à qui il va / est allé, ou pourquoi il est là.
function keyLine(journey: LeadJourney): string | null {
    switch (journey.status) {
        case 'waterfall_queued':
            return journey.offerName ? `→ ${journey.offerName}` : null;
        case 'waterfall_assigned':
        case 'bought_bourse':
            return journey.buyerEmail ? `→ ${journey.buyerEmail}` : null;
        case 'bourse_after_partner':
            return journey.refusal ? REFUSALS[journey.refusal] : null;
        case 'off_market':
            return journey.offMarketReason ? OFF_MARKET[journey.offMarketReason] || journey.offMarketReason : null;
        default:
            return null;
    }
}

// `children` = détail et actions du partenaire (cellule Batiweb), affichés seulement si `showActions`.
const WaterfallJourneyCell = ({ journey, children, showActions }: { journey?: LeadJourney | null; children?: React.ReactNode; showActions?: boolean }) => {
    if (!journey) return <span className="text-gray-300">—</span>;
    const config = STATUS[journey.status];
    const partner = journey.partner || 'Batiweb';
    const line = keyLine(journey);

    return (
        <div className="flex max-w-[240px] flex-col gap-1">
            <Frise journey={journey} />
            <div className="flex flex-wrap items-center gap-1">
                <HoverExplanation
                    title={config.label.replace('{p}', partner)}
                    trigger={
                        <span>
                            <StatusPill tone={config.tone} label={config.label.replace('{p}', partner)} live={config.live} />
                        </span>
                    }
                >
                    <p>{config.meaning}</p>
                    {journey.refusal === 'not_sent' && <p className="mt-1">Jamais proposé à {partner} : directement au waterfall</p>}
                    {journey.refusal && journey.refusal !== 'not_sent' && <p className="mt-1">Refus {partner} : {REFUSALS[journey.refusal]}</p>}
                    {journey.offerName && journey.status !== 'waterfall_queued' && <p className="mt-1">Offre : {journey.offerName}</p>}
                    {journey.at && <p className="mt-1 text-gray-400">Le {shortDate(journey.at)}</p>}
                </HoverExplanation>
                {journey.overdue && <StatusPill tone="amber" label="En retard" />}
            </div>
            {line && <span className="truncate pl-0.5 text-[11px] leading-4 text-[#596275] dark:text-[#AEB6C4]" title={line}>{line}</span>}
            {showActions && children}
        </div>
    );
};

export default WaterfallJourneyCell;
